import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface FileAttachment {
  file_name?: string;
  path?: string;
  size?: number;
  mime_type?: string;
  type?: string;
  url?: string; // computed server-side (helpers.AttachFileURLs) from `path`
}

interface IssueDetails {
  id: string;
  student_id: string;
  teacher_id: string;
  session_title: string;
  issue_type: string;
  description: string;
  resolved: boolean;
  resolved_time: string | null;
  user_viewed: boolean;
  user_viewed_time: string | null;
  user_notified: boolean;
  user_notified_time: string;
  created_at: string;
  updated_at: string;

  // ── Student ──
  student_name: string;
  student_email?: string;
  student_admission_no?: string;
  student_gender?: string;
  student_blood_group?: string;
  student_dob?: string;
  student_address?: string;
  student_father_name?: string;
  student_mother_name?: string;
  student_father_no?: string;
  student_mother_no?: string;
  student_roll_no?: number;
  student_section?: string;
  student_profile_picture?: FileAttachment | null;
  student_user_email?: string;
  class_name?: string;

  // ── Teacher ──
  teacher_name: string;
  teacher_contact?: string;
  teacher_qualification?: string;
  teacher_email?: string;
  teacher_profile_picture?: FileAttachment | null;

  // ── Resolver ──
  resolved_by_name?: string;
  resolved_by_email?: string;
}

interface ViewStudentIssueModalProps {
  open: boolean;
  onClose: () => void;
  issueId: string | null;
}

const ViewStudentIssueModal: React.FC<ViewStudentIssueModalProps> = ({ open, onClose, issueId }) => {
  const toast = useRef<Toast>(null);
  const [issueData, setIssueData] = useState<IssueDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const fetchIssueData = async () => {
      if (!issueId || !open) return;

      setLoading(true);
      setIsClosing(false);
      try {
        const response = await apiClient.get(`/api/admin/student-issues/${issueId}`);

        if (response.data.status === 'success') {
          const minLoadingTime = new Promise(resolve => setTimeout(resolve, 650));
          await minLoadingTime;
          setIssueData(response.data.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to fetch issue details',
            life: 5000
          });
          onClose();
        }
      } catch (error: any) {
        const message = error.response?.data?.message || error.message || 'Failed to fetch issue details';
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 5000
        });
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchIssueData();
    }
  }, [open, issueId, onClose]);

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) {
        dialog.classList.add('dialog-closing');
      }
      if (mask) {
        mask.classList.add('mask-closing');
      }
    }
  }, [isClosing]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(',', ' ');
  };

  const getIssueTypeStyle = (issueType: string) => {
    switch (issueType.toLowerCase()) {
      case 'academic':
        return { bg: '#3b82f6', text: 'Academic' };
      case 'attendance':
        return { bg: '#a855f7', text: 'Attendance' };
      case 'health':
        return { bg: '#10b981', text: 'Health' };
      case 'behavioral':
        return { bg: '#f59e0b', text: 'Behavioral' };
      default:
        return { bg: '#6b7280', text: issueType };
    }
  };

  const InfoRow = ({
    icon,
    label,
    value,
    isChip = false,
    chipColor = '#4169E1'
  }: {
    icon: string;
    label: string;
    value: string | number | undefined | null;
    isChip?: boolean;
    chipColor?: string;
  }) => {
    const display = value === undefined || value === null || value === '' ? '—' : value;
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '10px 0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: '10px',
          background: 'rgba(65, 105, 225, 0.08)',
          color: '#4169E1',
          flexShrink: 0
        }}>
          <i className={icon} style={{ fontSize: '15px' }}></i>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '11px',
            fontWeight: 700,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            marginBottom: '4px'
          }}>
            {label}
          </div>
          {isChip ? (
            <span style={{
              display: 'inline-block',
              padding: '5px 12px',
              borderRadius: '8px',
              background: chipColor,
              color: 'white',
              fontSize: '13px',
              fontWeight: 600,
              boxShadow: `0 2px 8px ${chipColor}40`
            }}>
              {display}
            </span>
          ) : (
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1f2937',
              lineHeight: 1.3,
              wordBreak: 'break-word'
            }}>
              {display}
            </div>
          )}
        </div>
      </div>
    );
  };

  const studentProfileUrl = issueData?.student_profile_picture?.url || null;
  const teacherProfileUrl = issueData?.teacher_profile_picture?.url || null;

  const classSection = issueData
    ? [issueData.class_name, issueData.student_section].filter(Boolean).join(' ')
    : '';

  const headerTemplate = () => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '8px 0'
    }}>
      <div style={{
        width: '54px',
        height: '54px',
        borderRadius: '14px',
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '44px!important',
        fontWeight: 700,
        boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)',
        flexShrink: 0,
        border: '3px solid white'
      }}>
        <i className="pi pi-exclamation-triangle"></i>
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: '20px',
          fontWeight: 700,
          color: '#1e40af',
          lineHeight: 1.2,
          marginBottom: '4px'
        }}>
          {issueData?.session_title || 'Issue Details'}
        </div>
        <div style={{
          fontSize: '13px',
          color: '#64748b',
          fontWeight: 500
        }}>
          Student Issue Information
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{
          width: '90vw',
          maxWidth: '800px',
          borderRadius: '24px',
          overflow: 'hidden'
        }}
        contentStyle={{
          padding: '18px',
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none'
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '16px 18px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px'
        }}
        modal
        dismissableMask
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }

          .p-dialog-mask.mask-closing {
            animation: maskFadeOut 0.4s ease !important;
          }

          @keyframes maskFadeIn {
            from {
              opacity: 0;
              background-color: rgba(0, 0, 0, 0);
            }
            to {
              opacity: 1;
              background-color: rgba(0, 0, 0, 0.25);
            }
          }

          @keyframes maskFadeOut {
            from {
              opacity: 1;
              background-color: rgba(0, 0, 0, 0.25);
            }
            to {
              opacity: 0;
              background-color: rgba(0, 0, 0, 0);
            }
          }

          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }

          .p-dialog.dialog-closing {
            animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }

          @keyframes dialogOpen {
            from {
              opacity: 0;
              transform: scale(0.92) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes dialogClose {
            from {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
            to {
              opacity: 0;
              transform: scale(0.88) translateY(25px);
            }
          }

          .p-dialog .p-dialog-content::-webkit-scrollbar {
            width: 8px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track {
            background: rgba(241, 245, 249, 0.5);
            border-radius: 10px;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb {
            background: rgba(148, 163, 184, 0.5);
            border-radius: 10px;
            transition: background 0.2s ease;
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover {
            background: rgba(100, 116, 139, 0.7);
          }
        `}</style>

        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            gap: '16px'
          }}>
            <ProgressSpinner
              style={{ width: '50px', height: '50px' }}
              strokeWidth="4"
            />
            <span style={{
              color: '#64748b',
              fontSize: '14px',
              fontWeight: 600
            }}>
              Loading issue details...
            </span>
          </div>
        ) : issueData ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Profile Pictures */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px 18px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                gap: '24px',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}>
                {/* Teacher Profile */}
                <div style={{ textAlign: 'center' }}>
                  {teacherProfileUrl ? (
                    <img
                      src={teacherProfileUrl}
                      alt="Teacher"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #e5e7eb',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginBottom: '8px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '32px',
                      fontWeight: 700,
                      border: '3px solid #e5e7eb',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      marginBottom: '8px'
                    }}>
                      {issueData.teacher_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1f2937',
                    marginBottom: '2px'
                  }}>
                    {issueData.teacher_name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#64748b',
                    fontWeight: 500
                  }}>
                    Teacher
                  </div>
                </div>

                {/* Arrow */}
                <div style={{
                  fontSize: '24px',
                  color: '#cbd5e1'
                }}>
                  <i className="pi pi-arrow-right"></i>
                </div>

                {/* Student Profile */}
                <div style={{ textAlign: 'center' }}>
                  {studentProfileUrl ? (
                    <img
                      src={studentProfileUrl}
                      alt="Student"
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        border: '3px solid #e5e7eb',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginBottom: '8px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '32px',
                      fontWeight: 700,
                      border: '3px solid #e5e7eb',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      marginBottom: '8px'
                    }}>
                      {issueData.student_name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1f2937',
                    marginBottom: '2px'
                  }}>
                    {issueData.student_name}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#64748b',
                    fontWeight: 500
                  }}>
                    {classSection ? `${classSection} • ` : ''}
                    {issueData.student_roll_no != null ? `Roll ${issueData.student_roll_no}` : 'Student'}
                  </div>
                </div>
              </div>
            </div>

            {/* Issue Details */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '16px 18px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                  color: 'white'
                }}>
                  <i className="pi pi-exclamation-triangle" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{
                  color: '#1e40af',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase'
                }}>
                  Issue Details
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                  <InfoRow
                    icon="pi pi-tag"
                    label="Issue Type"
                    value={getIssueTypeStyle(issueData.issue_type).text}
                    isChip={true}
                    chipColor={getIssueTypeStyle(issueData.issue_type).bg}
                  />
                  <InfoRow
                    icon="pi pi-check-circle"
                    label="Status"
                    value={issueData.resolved ? 'Resolved' : 'Unresolved'}
                    isChip={true}
                    chipColor={issueData.resolved ? '#10b981' : '#ef4444'}
                  />
                </div>

                <InfoRow
                  icon="pi pi-calendar"
                  label="Created At"
                  value={formatDateTime(issueData.created_at)}
                />

                {issueData.resolved_time && (
                  <InfoRow
                    icon="pi pi-calendar-times"
                    label="Resolved At"
                    value={formatDateTime(issueData.resolved_time)}
                  />
                )}

                {issueData.resolved && issueData.resolved_by_name && (
                  <InfoRow
                    icon="pi pi-user-edit"
                    label="Resolved By"
                    value={
                      issueData.resolved_by_email
                        ? `${issueData.resolved_by_name} (${issueData.resolved_by_email})`
                        : issueData.resolved_by_name
                    }
                  />
                )}

                {/* Issue Title & Description */}
                <div style={{
                  marginTop: '8px',
                  padding: '16px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.04)',
                  border: '1px solid rgba(59, 130, 246, 0.1)'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px'
                  }}>
                    Issue Title
                  </div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#1e40af',
                    marginBottom: '12px',
                    lineHeight: 1.3
                  }}>
                    {issueData.session_title}
                  </div>

                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '8px',
                    paddingTop: '12px',
                    borderTop: '1px solid rgba(229, 231, 235, 0.5)'
                  }}>
                    Description
                  </div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#1f2937',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {issueData.description}
                  </div>
                </div>
              </div>
            </div>

            {/* Student Info */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '16px 18px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white'
                }}>
                  <i className="pi pi-id-card" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{
                  color: '#1e40af',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase'
                }}>
                  Student Info
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                <InfoRow icon="pi pi-hashtag" label="Admission No" value={issueData.student_admission_no} />
                <InfoRow icon="pi pi-book" label="Class / Section" value={classSection || undefined} />
                <InfoRow icon="pi pi-list-check" label="Roll No" value={issueData.student_roll_no} />
                <InfoRow icon="pi pi-venus-mars" label="Gender" value={issueData.student_gender} />
                <InfoRow icon="pi pi-heart-fill" label="Blood Group" value={issueData.student_blood_group} />
                <InfoRow
                  icon="pi pi-calendar"
                  label="Date of Birth"
                  value={issueData.student_dob ? new Date(issueData.student_dob).toLocaleDateString('en-GB') : undefined}
                />
              </div>
              <InfoRow icon="pi pi-map-marker" label="Address" value={issueData.student_address} />
            </div>

            {/* Contact Information */}
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '16px 18px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
                paddingBottom: '12px',
                borderBottom: '1px solid rgba(229, 231, 235, 0.6)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
                  color: 'white'
                }}>
                  <i className="pi pi-phone" style={{ fontSize: '15px' }}></i>
                </div>
                <span style={{
                  color: '#1e40af',
                  fontWeight: 700,
                  fontSize: '14px',
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase'
                }}>
                  Contact Information
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                  <InfoRow
                    icon="pi pi-phone"
                    label="Teacher Contact"
                    value={issueData.teacher_contact}
                  />
                  <InfoRow
                    icon="pi pi-briefcase"
                    label="Teacher Qualification"
                    value={issueData.teacher_qualification}
                  />
                </div>
                <InfoRow
                  icon="pi pi-envelope"
                  label="Teacher Email"
                  value={issueData.teacher_email}
                />

                <div style={{
                  background: 'rgba(241, 245, 249, 0.6)',
                  borderRadius: '12px',
                  padding: '12px',
                  marginTop: '12px'
                }}>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <i className="pi pi-user" style={{ fontSize: '13px' }}></i>
                    Student Contact Information
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '6px' }}>
                    <InfoRow
                      icon="pi pi-user"
                      label="Father's Name"
                      value={issueData.student_father_name}
                    />
                    <InfoRow
                      icon="pi pi-phone"
                      label="Father's Contact"
                      value={issueData.student_father_no}
                    />
                    <InfoRow
                      icon="pi pi-user"
                      label="Mother's Name"
                      value={issueData.student_mother_name}
                    />
                    <InfoRow
                      icon="pi pi-phone"
                      label="Mother's Contact"
                      value={issueData.student_mother_no}
                    />
                  </div>
                  <InfoRow
                    icon="pi pi-envelope"
                    label="Student Email"
                    value={issueData.student_email}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Dialog>
    </>
  );
};

export default ViewStudentIssueModal;